<template>
  <div class="ccma-french-contribution">
    <!-- Full-screen loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-container">
        <Loader type="infinite-spinner" :size="'large'" />
        <p class="loading-text">{{ t('frenchContribution.LoadingDocuments') }}</p>
      </div>
    </div>

    <div v-if="showError" class="ccma-french-contribution-error">
      <h2>{{ t('frenchContribution.PageLoadError') }}</h2>
    </div>

    <!-- Error notification for save operations -->
    <div v-if="saveError" class="save-error-notification">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <div class="error-details">
          <h3>{{ t('frenchContribution.SaveError') }}</h3>
          <p>{{ saveErrorMessage }}</p>
        </div>
        <button class="error-close" @click="clearSaveError">×</button>
      </div>
      <div class="error-actions">
        <button class="baseBtn primaryBtn" @click="uploadDocument">{{ t('frenchContribution.TryAgain') }}</button>
      </div>
    </div>

    <!-- Success notification -->
    <div v-if="saveSuccess" class="save-success-notification">
      <div class="success-content">
        <div class="success-icon">✅</div>
        <div class="success-message">
          <p>{{ t('frenchContribution.SaveSuccess') }}</p>
        </div>
        <button class="success-close" @click="clearSaveSuccess">×</button>
      </div>
    </div>

    <div v-else-if="!showError && !isLoading">
      <!-- Header Section -->
      <div class="upload-header">
        <div class="first">
          <div class="upload-title">{{ t('frenchContribution.FrenchContributionHeader') }}</div>
          <div class="upload-subtitle mb-1">{{ t('frenchContribution.FrenchContributionSubHeader') }}</div>
        </div>
        <div class="upload-buttons">
          <button
            id="btnSave"
            class="baseBtn primaryBtn"
            :disabled="!selectedFile || isSaving"
            @click="uploadDocument"
          >
            {{ isSaving ? t('frenchContribution.Uploading') : t('frenchContribution.Upload') }}
          </button>
        </div>
      </div>

      <!-- SIRET Display Section -->
      <div v-if="siretNumber" class="siret-section">
        <div class="siret-container">
          <label class="siret-label">{{ t('frenchContribution.SiretNumber') }}</label>
          <input
            v-model="siretNumber"
            type="text"
            class="siret-input"
            readonly
            disabled
          />
        </div>
      </div>

      <!-- Upload Section -->
      <div class="upload-section">
        <div v-if="!selectedFile" class="upload-area" :class="{ 'drag-over': isDragOver }" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
          <input
            ref="fileInput"
            type="file"
            @change="onFileSelected"
            accept=".pdf,application/pdf"
            class="file-input"
            id="document-upload"
          />
          <label for="document-upload" class="upload-label">
            <div class="upload-content">
              <i class="fas fa-file-pdf"></i>
              <p>{{ t('frenchContribution.UploadFile') }}</p>
              <small>{{ t('frenchContribution.MaxFileSize') }}</small>
              <small>{{ t('frenchContribution.SupportedFormats') }}</small>
            </div>
          </label>
        </div>

        <!-- Selected File Preview -->
        <div v-if="selectedFile" class="selected-file">
          <p><strong>{{ t('frenchContribution.SelectedFile') }}:</strong> {{ selectedFile.name }}</p>
          <div class="file-info">
            <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
            <span class="file-type">{{ selectedFile.type }}</span>
          </div>
          <div v-if="fileError" class="error-message">{{ fileError }}</div>
          <button class="remove-file-btn" @click="removeSelectedFile" type="button">
            {{ t('frenchContribution.RemoveFile') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { Loader } from '@progress/kendo-vue-indicators'
import {
  PwsDocumentSaveWithLargeIntegerSupport,
  PwsDocumentFileSave,
  PwsBeeTaxPayerGovermentIdGet
} from '../../api/ConsultantCorner'
import { GetCountryCode, GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore'
import type { IBusinessDetails, IPwsDocumentSaveRequest, IPwsBeeTaxPayerGovermentIdGetRequest } from '../../types/ConsultantCornerTypes'

const { t } = useI18n()
const logger = useLogger()

const props = defineProps<{
  businessDetails: IBusinessDetails | null
}>()

const emit = defineEmits<{
  updateDetails: [details: Partial<IBusinessDetails>]
}>()

// State management
const isLoading = ref(false)
const showError = ref(false)
const saveError = ref(false)
const saveSuccess = ref(false)
const saveErrorMessage = ref('')
const isSaving = ref(false)
const countryCode = ref('')
const siretNumber = ref('')

// File handling
const selectedFile = ref<File | null>(null)
const fileError = ref<string>('')
const fileInput = ref<HTMLInputElement>()
const isDragOver = ref(false)

// Check if user is French consultant
const checkCountryCode = async () => {
  try {
    countryCode.value = await GetCountryCode()
    logger.info('Country code loaded:', countryCode.value)
  } catch (error) {
    logger.error('Error loading country code:', error)
    countryCode.value = 'US' // Default fallback
  }
}

// Load SIRET if available
const loadSiretNumber = async () => {
  try {
    const country = await GetCountryCode()
    const request: IPwsBeeTaxPayerGovermentIdGetRequest = {
      govermentIdType: 'SIRET',
      countryCode: country
    }

    const response = await PwsBeeTaxPayerGovermentIdGet(request)
    logger.info('SIRET response:', response)

    if (response.item && response.item.govermentId && response.item.govermentId.trim() !== '') {
      siretNumber.value = response.item.govermentId
    }
  } catch (error) {
    logger.error('Error loading SIRET:', error)
    // Don't show error to user, just log it
  }
}

onMounted(async () => {
  await checkCountryCode()
  await loadSiretNumber()
})

// File validation
const validateFile = (file: File): boolean => {
  fileError.value = ''

  const maxSize = 10 * 1024 * 1024 // 10MB
  if (file.size > maxSize) {
    fileError.value = t('frenchContribution.FileTooLarge')
    return false
  }

  const allowedTypes = ['application/pdf']
  if (!allowedTypes.includes(file.type)) {
    fileError.value = t('frenchContribution.InvalidFileFormat')
    return false
  }

  return true
}

const onFileSelected = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    const file = target.files[0]
    processSelectedFile(file)
  }
}

const processSelectedFile = (file: File) => {
  if (validateFile(file)) {
    selectedFile.value = file
  } else {
    selectedFile.value = null
  }
}

// Drag and drop handlers
const onDragOver = () => {
  isDragOver.value = true
}

const onDragLeave = () => {
  isDragOver.value = false
}

const onDrop = (event: DragEvent) => {
  isDragOver.value = false
  const files = event.dataTransfer?.files
  if (files && files[0]) {
    processSelectedFile(files[0])
  }
}

const removeSelectedFile = () => {
  selectedFile.value = null
  fileError.value = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Creates the document entity, then uploads the file bytes. The backend routes the
// upload to the correct S3 based on the document category on the request.
const saveDocumentToDestination = async (request: IPwsDocumentSaveRequest) => {
  const documentSaveResponse = await PwsDocumentSaveWithLargeIntegerSupport(request)
  if (documentSaveResponse.beeDocumentGuid && selectedFile.value) {
    await PwsDocumentFileSave(documentSaveResponse.beeDocumentGuid, selectedFile.value)
  }
}

const uploadDocument = async () => {
  if (!selectedFile.value) return

  try {
    isSaving.value = true
    saveError.value = false
    saveErrorMessage.value = ''

    // Get bee number
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (!beeNumber) {
      throw new Error(t('errors.unableToGetBeeNumber'))
    }

    const baseRequest = {
      beeNumber: beeNumber,
      documentDefinition: '',
      documentName: selectedFile.value.name,
      userName: '', // Not required according to instructions
      createdDate: new Date().toISOString().split('.')[0] // Remove milliseconds for API compatibility
    }

    // Upload the document twice:
    //  1) category 'NONE'          -> routed to TTG's S3 (keeps a record on our side for troubleshooting)
    //  2) category 'FRENCHCONTRIB' -> routed by the backend to the client's own S3
    await saveDocumentToDestination({ ...baseRequest, documentCategoryId: 'NONE' })
    await saveDocumentToDestination({ ...baseRequest, documentCategoryId: 'FRENCHCONTRIB' })

    // Clear the selected file
    removeSelectedFile()

    // Show success notification
    saveSuccess.value = true
    setTimeout(() => {
      saveSuccess.value = false
    }, 5000)

    logger.info('French contribution document uploaded to both destinations')
  } catch (error) {
    logger.error('Error uploading document:', error)
    saveError.value = true
    saveErrorMessage.value = error instanceof Error ? error.message : t('frenchContribution.UnknownError')
  } finally {
    isSaving.value = false
  }
}

// Error and success handling
const clearSaveError = () => {
  saveError.value = false
  saveErrorMessage.value = ''
}

const clearSaveSuccess = () => {
  saveSuccess.value = false
}
</script>

<style lang="scss" scoped>
.ccma-french-contribution {
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
}

/* Loading overlay styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(white, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: $z-loading-overlay;
}

.loading-container {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: $radius-md;
  box-shadow: 0 4px 6px rgba(black, 0.1);
}

.loading-text {
  @include typography-body-large;
  margin-top: 1rem;
  color: #666;
}

/* Error page styles */
.ccma-french-contribution-error {
  padding: 2rem;
  text-align: center;
  color: #dc3545;
}

/* Notification styles */
.save-error-notification,
.save-success-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: $z-notification;
  max-width: 400px;
  border-radius: $radius-md;
  box-shadow: 0 4px 12px rgba(black, 0.2);
  animation: slideIn 0.3s ease-out;
}

.save-error-notification {
  border-left: 4px solid #dc3545;
}

.save-success-notification {
  border-left: 4px solid #28a745;
}

.error-content,
.success-content {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
}

.error-icon,
.success-icon {
  @include typography-h3;
  margin-right: 0.75rem;
  margin-top: 0.25rem;
}

.error-details,
.success-message {
  flex: 1;
}

.error-details h3 {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 0.5rem 0;
  color: #dc3545;
}

.error-details p,
.success-message p {
  @include typography-body;
  margin: 0;
  color: #666;
}

.error-close,
.success-close {
  @include typography-h3;
  background: none;
  border: none;
  cursor: pointer;
  color: #999999;
  padding: 0;
  margin-left: 0.5rem;
}

.error-close:hover,
.success-close:hover {
  color: #666;
}

.error-actions {
  padding: 0 1rem 1rem 1rem;
  border-top: 1px solid #e9ecef;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
}

/* Header styles */
.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.upload-title {
  @include typography-h3;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 0.5rem;
}

.upload-subtitle {
  @include typography-body;
  color: #666;
}

.upload-buttons {
  display: flex;
  gap: 0.75rem;
}

/* SIRET section */
.siret-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
}

.siret-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.siret-label {
  @include typography-body;
  @include font-weight-semibold;
  color: #333;
}

.siret-input {
  padding: 0.75rem;
  @include typography-body;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  background-color: #e9ecef;
  color: #495057;
  cursor: not-allowed;
  font-family: monospace;
  letter-spacing: 0.5px;
}

.siret-input:disabled {
  opacity: 0.8;
}

/* Upload section */
.upload-section {
  margin-bottom: 2rem;
}

.upload-area {
  border: 2px dashed #e0e0e0;
  border-radius: $radius-md;
  padding: 3rem 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  background: #fefefe;
}

.upload-area:hover,
.upload-area.drag-over {
  border-color: #e67e22;
  background: #fff8f0;
}

.file-input {
  display: none;
}

.upload-label {
  cursor: pointer;
  display: block;
}

.upload-content i {
  @include typography-display;
  color: #e67e22;
  margin-bottom: 1rem;
  display: block;
}

.upload-content p {
  @include typography-h4;
  @include font-weight-medium;
  margin-bottom: 0.75rem;
  color: #333;
}

.upload-content small {
  @include typography-body;
  display: block;
  color: #666;
  margin-bottom: 0.25rem;
}

/* Selected file styles */
.selected-file {
  padding: 1.5rem;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
  margin-bottom: 1.5rem;
}

.selected-file p {
  @include typography-body;
  margin-bottom: 0.75rem;
}

.file-info {
  @include typography-body;
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  color: #666;
}

.file-size,
.file-type {
  padding: 0.25rem 0.5rem;
  background: #e9ecef;
  border-radius: $radius-sm;
  font-family: monospace;
}

.remove-file-btn {
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  @include typography-body;
  border-radius: $radius-sm;
  cursor: pointer;
  margin-top: 0.5rem;
}

.remove-file-btn:hover {
  background: #c82333;
}

/* Error message styles */
.error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: $radius-sm;
}

/* Button styles */
.baseBtn {
  @include typography-body;
  @include font-weight-medium;
  padding: 0.75rem 1.5rem;
  border-radius: $button-radius;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}


.primaryBtn:disabled {
  background-color: #cccccc;
  border-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Animation */
@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive design */
@media (max-width: 768px) {
  .upload-header {
    flex-direction: column;
    gap: 1rem;
  }

  .upload-buttons {
    width: 100%;
  }

  .baseBtn {
    width: 100%;
  }

  .upload-area {
    padding: 2rem 1rem;
  }

  .selected-file {
    padding: 1rem;
  }

  .save-error-notification,
  .save-success-notification {
    left: 10px;
    right: 10px;
    max-width: none;
  }
}

@media (max-width: 480px) {
  .upload-content i {
    @include typography-h2;
  }

  .upload-content p {
    @include typography-body;
  }
}
</style>
